import { useCallback, useMemo } from 'react'
import NavTable from '../table/NavTable'
import useEntityList from '../../hooks/pagination/useEntityList'
import useInfiniteScroll from '../../hooks/pagination/useInfiniteScroll'
import { listSearchrooms } from '../../services/searchrooms'
import { searchroomLabel } from './searchroomLabel'

// The searchroom list in the nav column: a keyset-paginated, infinite-scrolling
// single column of searchroom names. `reloadSignal` (bumped after create/edit/
// delete) forces a reset back to the first page. `filters` (already mapped to
// service params) and `sort` narrow/order the list server-side; changing either
// resets pagination.
function SearchroomList({ reloadSignal, selectedId, onSelect, filters, sort, filtered }) {
  const fetchPage = useCallback(
    ({ cursor, limit, params: p }) =>
      listSearchrooms({ cursor, limit, sort: p?.sort, facets: p?.filters }).then((data) => ({
        items: data.searchrooms,
        nextCursor: data.nextCursor,
        hasMore: data.hasMore,
      })),
    []
  )

  const params = useMemo(
    () => ({ reload: reloadSignal, sort, filters }),
    [reloadSignal, sort, filters]
  )
  const { items, loading, loadingMore, hasMore, error, loadMore } = useEntityList({
    fetchPage,
    params,
    pageSize: 30,
  })

  const sentinelRef = useInfiniteScroll(loadMore, { enabled: hasMore && !loading })

  const columns = useMemo(
    () => [
      {
        key: 'label',
        header: 'Searchroom',
        render: (room) => {
          const label = searchroomLabel(room)
          return (
            <span className="nav-ellipsis" title={label}>
              {label}
            </span>
          )
        },
      },
    ],
    []
  )

  if (loading) return <div className="nav-table-status">Loading…</div>
  if (error) return <div className="nav-table-status">{error}</div>

  return (
    <>
      <NavTable
        columns={columns}
        rows={items}
        getRowId={(room) => room._id}
        selectedId={selectedId}
        onRowClick={onSelect}
        showHeader={false}
        emptyLabel={
          filtered
            ? 'No searchrooms match these filters.'
            : 'No searchrooms yet. Create one to get started.'
        }
      />
      {hasMore && (
        <div ref={sentinelRef} className="nav-table-status">
          {loadingMore ? 'Loading…' : ''}
        </div>
      )}
    </>
  )
}

export default SearchroomList
